import { Space, Button, Badge, Switch } from 'antd';
import { MessageOutlined, VideoCameraOutlined, CalendarOutlined, BellOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useNodeInfo } from '../../hooks/use_node_info';
import './EntityComms.css';

function EntityComms({ entityId, entity, isAdmin, notifEnabled, onToggleNotif, onSchedule, onCancelHangout, onBreakout, style, messagesPath }) {
    const navigate = useNavigate();
    const { featureHangouts, featureHangoutSchedule, featureHangoutFacilitator, featureNotifications } = useNodeInfo();
    const resolvedMessagesPath = messagesPath ?? `/messages/${entityId}`;
    const hideMessages = !!messagesPath;

    const sessions = entity.attributes?.scheduled_hangouts || [];
    const now = dayjs();
    const upcomingSessions = sessions
        .map(s => ({ ...s, dt: dayjs(s.scheduled_at) }))
        .filter(s => s.dt.isAfter(now))
        .sort((a, b) => a.dt.valueOf() - b.dt.valueOf());

    return (
        <div className="entity-comms" style={style}>
            <div className="entity-comms-row">
                {/* Messages */}
                {!hideMessages && (
                    <div className="entity-comms-cell">
                        <Button size="small" icon={<MessageOutlined />} onClick={() => navigate(resolvedMessagesPath)}>Messages</Button>
                    </div>
                )}

                {/* Hangout */}
                {featureHangouts && (
                    <div className="entity-comms-cell">
                        <Space size={6}>
                            <Badge dot={entity.hangout_active} color="green">
                                <Button
                                    size="small"
                                    icon={<VideoCameraOutlined />}
                                    type={entity.hangout_active ? 'primary' : 'default'}
                                    onClick={() => window.open(`/entity/${entityId}/hangout`, '_blank', 'noopener,noreferrer')}
                                >
                                    {entity.hangout_active ? `${entity.hangout_participant_count} in room` : 'Call'}
                                </Button>
                            </Badge>
                            {isAdmin && featureHangoutSchedule && (
                                <Button size="small" icon={<CalendarOutlined />} onClick={onSchedule}>Schedule</Button>
                            )}
                            {isAdmin && featureHangoutFacilitator && (
                                <Button size="small" icon={<ThunderboltOutlined />} onClick={onBreakout}>Breakout</Button>
                            )}
                        </Space>
                    </div>
                )}

                {/* Notifications — hidden entirely on a node with the feature off,
                    where the preference routes behind this switch aren't mounted. */}
                {featureNotifications && (
                    <div className="entity-comms-cell">
                        <BellOutlined
                            className="entity-comms-icon"
                            style={{ opacity: notifEnabled ? 1 : 0.35 }}
                        />
                        <Switch size="small" checked={notifEnabled} onChange={onToggleNotif} />
                    </div>
                )}
            </div>

            {featureHangouts && featureHangoutSchedule && upcomingSessions.map(session => {
                const canJoin = now.isAfter(session.dt.subtract(30, 'minute'));
                return (
                    <div key={session.id} className="entity-comms-session">
                        <CalendarOutlined style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: 'var(--fs-sm)' }}>
                            {session.title ? <strong>{session.title}</strong> : 'Hangout'}
                            <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>
                                {session.dt.format('D MMM YYYY [at] HH:mm')}
                            </span>
                        </span>
                        <Space size={6}>
                            <Button
                                size="small"
                                type={canJoin ? 'primary' : 'default'}
                                disabled={!canJoin}
                                onClick={() => window.open(`/entity/${entityId}/hangout`, '_blank', 'noopener,noreferrer')}
                            >
                                Join
                            </Button>
                            {isAdmin && (
                                <Button size="small" danger onClick={() => onCancelHangout(session.id)}>
                                    Cancel
                                </Button>
                            )}
                        </Space>
                    </div>
                );
            })}
        </div>
    );
}

export default EntityComms;
